import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Bot, Radio, GraduationCap } from "lucide-react";
import { MaskedLine, Reveal, Eyebrow } from "@/components/Reveal";
import { PRICING_GROUPS, YEARLY_DISCOUNT_LABEL } from "@/data/pricing";

const GROUP_ICONS = { bots: Bot, signals: Radio, education: GraduationCap };

const BillingToggle = ({ yearly, onChange }) => (
    <div
        data-testid="pricing-billing-toggle"
        className="inline-flex items-center border border-white/15 bg-coal/40 p-1"
    >
        <button
            data-testid="pricing-toggle-monthly"
            onClick={() => onChange(false)}
            className={`px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                !yearly ? "bg-gold text-black" : "text-zinc-400 hover:text-white"
            }`}
        >
            Monthly
        </button>
        <button
            data-testid="pricing-toggle-yearly"
            onClick={() => onChange(true)}
            className={`px-6 py-2.5 font-mono text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
                yearly ? "bg-gold text-black" : "text-zinc-400 hover:text-white"
            }`}
        >
            Yearly <span className={yearly ? "text-black/60" : "text-gold"}>· {YEARLY_DISCOUNT_LABEL}</span>
        </button>
    </div>
);

const TierCard = ({ tier, group, yearly, delay }) => {
    const oneTime = group.billing === "one-time";
    const price = oneTime ? tier.oneTime : yearly ? tier.yearly : tier.monthly;
    const suffix = oneTime ? "one-time" : "/mo";

    return (
        <Reveal delay={delay} className="h-full">
            <div
                data-testid={`pricing-tier-${tier.id}`}
                className={`relative flex h-full flex-col border p-8 md:p-10 transition-colors duration-300 ${
                    tier.featured
                        ? "border-gold/50 bg-gold/[0.04]"
                        : "border-white/10 bg-coal/30 hover:border-white/25"
                }`}
            >
                {tier.featured && (
                    <span className="absolute -top-3 left-8 bg-gold px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-black">
                        Most popular
                    </span>
                )}
                <h3 className="font-display text-2xl font-extrabold tracking-tight">{tier.name}</h3>
                <p className="mt-2 font-editorial italic font-light text-lg text-zinc-400">{tier.summary}</p>
                <div className="mt-8 flex items-baseline gap-2">
                    <span
                        data-testid={`pricing-price-${tier.id}`}
                        className="font-mono text-4xl md:text-5xl font-medium tracking-tight text-white"
                    >
                        ${price}
                    </span>
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">{suffix}</span>
                </div>
                {!oneTime && (
                    <p className="mt-2 font-mono text-[11px] text-zinc-500">
                        {yearly ? `Billed annually — $${tier.yearly * 12}/yr` : "Billed monthly, cancel anytime"}
                    </p>
                )}
                {oneTime && (
                    <p className="mt-2 font-mono text-[11px] text-zinc-500">Lifetime access included</p>
                )}
                <ul className="mt-8 flex-1 space-y-3">
                    {tier.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                            <Check size={15} className="mt-0.5 shrink-0 text-gold" />
                            {f}
                        </li>
                    ))}
                </ul>
                <Link
                    to={`/contact?interest=${encodeURIComponent(group.interest)}`}
                    data-testid={`pricing-enquire-${tier.id}`}
                    className={`mt-10 inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm transition-colors duration-300 ${
                        tier.featured
                            ? "bg-gold font-medium text-black hover:bg-gold-light"
                            : "border border-gold/40 text-gold hover:bg-gold hover:text-black"
                    }`}
                >
                    Enquire about {tier.name} <ArrowUpRight size={15} />
                </Link>
            </div>
        </Reveal>
    );
};

export default function Pricing() {
    const [yearly, setYearly] = useState(false);

    return (
        <>
            <section data-testid="pricing-hero" className="relative overflow-hidden">
                <div className="pointer-events-none absolute -top-40 left-[-10%] h-[30rem] w-[30rem] rounded-full bg-[#D4AF37]/10 blur-3xl" />
                <div className="mx-auto max-w-7xl px-6 pt-40 pb-16 md:px-10 md:pt-48 md:pb-20">
                    <MaskedLine delay={0.05}>
                        <Eyebrow>Pricing</Eyebrow>
                    </MaskedLine>
                    <h1 className="mt-8 max-w-4xl font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-balance">
                        <MaskedLine delay={0.2}>Transparent terms.</MaskedLine>
                        <MaskedLine delay={0.34}>
                            <span className="font-editorial italic font-light text-gold">No surprises.</span>
                        </MaskedLine>
                    </h1>
                    <Reveal delay={0.6} className="mt-10 max-w-2xl">
                        <p className="text-base md:text-lg leading-relaxed text-zinc-400">
                            Every plan ends the same way — a conversation with the desk. Compare the tiers
                            below, then enquire and we will confirm fit, onboarding and current availability.
                        </p>
                    </Reveal>
                    <Reveal delay={0.75} className="mt-12">
                        <BillingToggle yearly={yearly} onChange={setYearly} />
                    </Reveal>
                </div>
            </section>

            <section className="mx-auto max-w-7xl px-6 pb-28 md:px-10 md:pb-40">
                <div className="space-y-24 md:space-y-32">
                    {PRICING_GROUPS.map((group, gi) => {
                        const Icon = GROUP_ICONS[group.id];
                        return (
                            <div key={group.id} id={group.id} data-testid={`pricing-group-${group.id}`} className="scroll-mt-32">
                                <Reveal>
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-11 w-11 items-center justify-center border border-gold/30 text-gold">
                                            <Icon size={19} />
                                        </span>
                                        <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">
                                            0{gi + 1} — {group.billing === "one-time" ? "One-time enrolment" : "Subscription"}
                                        </span>
                                    </div>
                                    <h2 className="mt-6 font-display text-3xl md:text-4xl font-extrabold tracking-tight">
                                        {group.name}
                                    </h2>
                                    <p className="mt-4 max-w-2xl text-base leading-relaxed text-zinc-400">{group.blurb}</p>
                                </Reveal>
                                <div
                                    className={`mt-12 grid gap-6 ${
                                        group.tiers.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2 lg:max-w-4xl"
                                    }`}
                                >
                                    {group.tiers.map((tier, ti) => (
                                        <TierCard key={tier.id} tier={tier} group={group} yearly={yearly} delay={ti * 0.1} />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <Reveal className="mt-20">
                    <p data-testid="pricing-disclaimer" className="max-w-3xl font-mono text-[11px] leading-relaxed text-zinc-600">
                        All prices in USD. Purchases are completed through an enquiry with our desk — we
                        confirm suitability before onboarding any client. Trading leveraged products carries
                        a high level of risk; never commit capital you cannot afford to lose.
                    </p>
                </Reveal>
            </section>

            <section data-testid="pricing-cta" className="border-t border-white/10 bg-coal/30">
                <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
                    <Reveal className="flex flex-wrap items-center justify-between gap-10">
                        <div>
                            <Eyebrow>Need something bespoke?</Eyebrow>
                            <h2 className="mt-5 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.05]">
                                Let's structure it.
                            </h2>
                            <p className="mt-4 max-w-lg text-sm leading-relaxed text-zinc-400">
                                Managing size, multiple accounts, or a fund mandate? The desk quotes custom
                                terms for institutional and high-volume clients.
                            </p>
                        </div>
                        <Link
                            to="/contact"
                            data-testid="pricing-cta-contact"
                            className="inline-flex items-center gap-2 rounded-full bg-gold px-10 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-gold-light"
                        >
                            Talk to the desk <ArrowUpRight size={16} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
